import type React from "react"

const PROJECT_STATUSES = {
  PLANNING: 1,
  READY_TO_START: 2,
  IN_PROGRESS: 3,
  ON_HOLD: 4,
  COMPLETED: 5,
  CANCELLED: 6
} as const

type ProjectStatusesKeys = keyof typeof PROJECT_STATUSES
type ProjectStatuses = (typeof PROJECT_STATUSES)[ProjectStatusesKeys]

const PROJECT_STATUSES_OBJ: Record<
  string,
  {
    id: ProjectStatuses
    value: ProjectStatuses
    label: string
    style: React.CSSProperties
  }
> = {
  [PROJECT_STATUSES.PLANNING]: {
    id: PROJECT_STATUSES.PLANNING,
    value: PROJECT_STATUSES.PLANNING,
    label: "Planning",
    style: {
      color: "#9C27B0",
      backgroundColor: "#F3E5F5",
      borderColor: "#F3E5F5"
    }
  },
  [PROJECT_STATUSES.READY_TO_START]: {
    id: PROJECT_STATUSES.READY_TO_START,
    value: PROJECT_STATUSES.READY_TO_START,
    label: "Ready to Start",
    style: {
      color: "#2196F3",
      backgroundColor: "#E3F2FD",
      borderColor: "#E3F2FD"
    }
  },
  [PROJECT_STATUSES.IN_PROGRESS]: {
    id: PROJECT_STATUSES.IN_PROGRESS,
    value: PROJECT_STATUSES.IN_PROGRESS,
    label: "In Progress",
    style: {
      color: "#4CAF50",
      backgroundColor: "#E8F5E9",
      borderColor: "#E8F5E9"
    }
  },
  [PROJECT_STATUSES.ON_HOLD]: {
    id: PROJECT_STATUSES.ON_HOLD,
    value: PROJECT_STATUSES.ON_HOLD,
    label: "On Hold",
    style: {
      color: "#FF9800",
      backgroundColor: "#FFF3E0",
      borderColor: "#FFF3E0"
    }
  },
  [PROJECT_STATUSES.COMPLETED]: {
    id: PROJECT_STATUSES.COMPLETED,
    value: PROJECT_STATUSES.COMPLETED,
    label: "Completed",
    style: {
      color: "#009688",
      backgroundColor: "#E0F2F1",
      borderColor: "#E0F2F1"
    }
  },
  [PROJECT_STATUSES.CANCELLED]: {
    id: PROJECT_STATUSES.CANCELLED,
    value: PROJECT_STATUSES.CANCELLED,
    label: "Cancelled",
    style: {
      color: "#616d73",
      backgroundColor: "#ECEFF1",
      borderColor: "#ECEFF1"
    }
  }
}

const PROJECT_STATUSES_MAP = Object.fromEntries(
  Object.values(PROJECT_STATUSES_OBJ).map((item) => [item.label, item])
) as Record<
  string,
  (typeof PROJECT_STATUSES_OBJ)[keyof typeof PROJECT_STATUSES_OBJ]
>

const ACTIVE_STATUSES = [
  PROJECT_STATUSES.PLANNING,
  PROJECT_STATUSES.READY_TO_START,
  PROJECT_STATUSES.IN_PROGRESS
] as const

const INACTIVE_STATUSES = [
  PROJECT_STATUSES.COMPLETED,
  PROJECT_STATUSES.ON_HOLD,
  PROJECT_STATUSES.CANCELLED
] as const

// Setting either archives the project, so a list that excludes the Archive can
// never hold one. They stay selectable — that move is the way in.
const ARCHIVING_STATUSES = [
  PROJECT_STATUSES.COMPLETED,
  PROJECT_STATUSES.CANCELLED
] as const

// A stored filter holds the numbers, a query param holds their strings, and an
// option's value is whatever the API sent — hence both shapes, not a coercion.
const isArchivingStatus = (value: unknown): boolean =>
  ARCHIVING_STATUSES.some(
    (status) => status === value || String(status) === String(value)
  )

// Shape-preserving: the stored filter is an array, the serialised param a
// comma-joined string, and null is "no status filter left" for both.
const withoutArchivingStatuses = <T>(
  value: string | readonly T[] | null | undefined
): string | T[] | null => {
  if (Array.isArray(value)) {
    const kept = value.filter((entry) => !isArchivingStatus(entry))
    return kept.length ? kept : null
  }
  const kept = String(value ?? "")
    .split(",")
    .map((entry) => entry.trim())
    .filter((entry) => entry && !isArchivingStatus(entry))
  return kept.length ? kept.join(",") : null
}

export {
  PROJECT_STATUSES,
  PROJECT_STATUSES_OBJ,
  PROJECT_STATUSES_MAP,
  INACTIVE_STATUSES,
  ACTIVE_STATUSES,
  isArchivingStatus,
  withoutArchivingStatuses,
  type ProjectStatuses
}
